import React, { useState } from 'react';
import {
  Table,
  Button,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Modal,
  CustomInput,
} from 'reactstrap';
import { TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ModalConsumer from '../../utility/Modal/ModalConsumer';
import { useAuthStore } from '../../../store/auth.store';
import { useToast } from '../../../contexts/ToastContext';

const useStyles = makeStyles((theme) => ({
  memberSearchInput: {
    marginBottom: '0 !important',
    '& input': {
      color: 'white',
      backgroundColor: 'transparent',
      minHeight: '32px',
      fontFamily: 'Poppins',
      padding: '4px 8px',
      fontSize: 'var(--menu-font-size)'
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'transparent',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.1) !important',
        top: 0,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.2) !important',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.2) !important',
      },
    },
    '& input::placeholder': {
      color: 'rgba(255,255,255,0.5)',
      opacity: 1,
      fontFamily: 'Poppins',
    }
  }
}));

const ConfirmModal = (props) => {
  const [modal, setModal] = useState(props.isOpen);
  const toggle = () => {
    setModal(!modal);
  };
  const remove = () => {
    props.onRequestClose();
  };
  const callback = () => {
    props.callback();
    toggle();
  };
  return (
    <div>
      <Modal isOpen={modal} toggle={toggle} onClosed={remove} contentClassName="delete-confirm-modal">
        <ModalHeader toggle={toggle}>{props.title}</ModalHeader>
        <ModalBody>{props.body}</ModalBody>
        <ModalFooter>
          <Button
            className="ml-auto btn-warning-custom"
            onClick={callback}
            style={{ position: 'relative', zIndex: 2 }}
          >
            <span style={{ position: 'relative', zIndex: 3 }}>Continue</span>
          </Button>
          <Button
            className="ml-2 btn-primary-custom"
            onClick={toggle}
            style={{ position: 'relative', zIndex: 2 }}
          >
            <span style={{ position: 'relative', zIndex: 3 }}>Cancel</span>
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

const TableHeader = ({ label, sortKey, orderBy, onSort }) => {
  const isActive = orderBy.key === sortKey;
  const sortIcon = isActive ? (orderBy.direction === 'asc' ? '↓' : '↑') : '↕';

  return (
    <th>
      <h4 onClick={() => onSort(sortKey)} className="mb-0">
        {label} {sortIcon}
      </h4>
    </th>
  );
};

const MembersList = ({ members, firebase, orgId, onRefresh }) => {
  const classes = useStyles();
  const [orderBy, setOrderBy] = useState({ key: 'email', direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const auth = useAuthStore((state) => state.auth);
  const { showSuccess, showError } = useToast();

  const handleSort = (key) => {
    setOrderBy(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const filteredMembers = [...members].filter(member => {
    if (!searchTerm) return true;
    return (
      member.email?.toLowerCase().includes(searchTerm) ||
      member.username?.toLowerCase().includes(searchTerm) ||
      member.role?.toLowerCase().includes(searchTerm)
    );
  });

  const sortedMembers = filteredMembers.sort((a, b) => {
    const aValue = a[orderBy.key] || '';
    const bValue = b[orderBy.key] || '';

    return orderBy.direction === 'asc'
      ? aValue.toString().localeCompare(bValue.toString())
      : bValue.toString().localeCompare(aValue.toString());
  });

  const toggleMemberNotification = async (memberId, notifKey, currentValue, memberName) => {
    try {
      const currentPrefs = members.find(m => m.id === memberId)?.notifications || { licenseExpiry: true };
      const updatedPrefs = { ...currentPrefs, [notifKey]: !currentValue };
      await firebase.updateMemberNotificationPreferences(orgId, memberId, updatedPrefs);
      showSuccess(`${memberName}'s license alerts ${!currentValue ? 'enabled' : 'disabled'}`);
      onRefresh();
    } catch (err) {
      console.error('Error updating notification preference:', err);
      showError(`Error updating preference: ${err.message}`);
    }
  };

  const removeMember = async (memberId, memberName) => {
    // Don't allow removing yourself
    if (memberId === auth?.uid) {
      showError('You cannot remove yourself from the organization.');
      return;
    }

    try {
      await firebase.removeMember(orgId, memberId);
      showSuccess(`${memberName} has been removed from the organization`);
      onRefresh();
    } catch (err) {
      console.error('Error removing member:', err);
      showError(`Error removing member: ${err.message}`);
    }
  };

  const updateMemberRole = async (memberId, newRole, memberName) => {
    try {
      await firebase.updateMemberRole(orgId, memberId, newRole);
      showSuccess(`${memberName}'s role updated to ${newRole}`);
      onRefresh();
    } catch (err) {
      console.error('Error updating member role:', err);
      showError(`Error updating role: ${err.message}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search members by email, username, or role..."
          value={searchTerm}
          onChange={handleSearch}
          className={classes.memberSearchInput}
        />
      </div>
      <div className="table-scroll-container" style={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'auto' }}>
        <Table className="table-tracks" responsive>
          <thead>
            <tr>
              <TableHeader
                label="Email"
                sortKey="email"
                orderBy={orderBy}
                onSort={handleSort}
              />
              <TableHeader
                label="User Name"
                sortKey="username"
                orderBy={orderBy}
                onSort={handleSort}
              />
              <TableHeader
                label="Role"
                sortKey="role"
                orderBy={orderBy}
                onSort={handleSort}
              />
              <TableHeader
                label="Joined"
                sortKey="joinedAt"
                orderBy={orderBy}
                onSort={handleSort}
              />
              <th><h4 className="mb-0">License Alerts</h4></th>
              <th>&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {sortedMembers.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center text-muted py-4">
                  No members found
                </td>
              </tr>
            ) : (
              sortedMembers.map((member, idx) => (
                <tr key={member.id} className={idx % 2 === 0 ? 'table-alt-row' : ''}>
                  <td>{member.email || '-'}</td>
                  <td>{member.username || '-'}</td>
                  <td>
                    {member.isOwner ? (
                      <span
                        style={{
                          color: '#ffffff',
                          fontSize: '14px',
                          fontFamily: 'var(--font-family-primary)',
                          padding: '4px 8px',
                        }}
                      >
                        Owner
                      </span>
                    ) : (
                      <CustomInput
                        type="select"
                        id={`role-select-${member.id}`}
                        value={member.role || 'USER'}
                        onChange={(e) => updateMemberRole(member.id, e.target.value, member.username || member.email)}
                        disabled={member.id === auth?.uid}
                        style={{
                          backgroundColor: 'transparent',
                          color: '#ffffff',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: 'var(--border-radius-sm)',
                          padding: '4px 24px 4px 8px',
                          cursor: member.id === auth?.uid ? 'not-allowed' : 'pointer',
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='4' height='5' viewBox='0 0 4 5'%3e%3cpath fill='%23ffffff' d='M2 0l2 2H0zm0 5L0 3h4z'/%3e%3c/svg%3e")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 0.5rem center',
                          backgroundSize: '12px 10px',
                          appearance: 'none',
                          fontSize: '14px',
                          fontFamily: 'var(--font-family-primary)',
                          transition: 'all var(--transition-fast)',
                          outline: 'none',
                          opacity: member.id === auth?.uid ? 0.6 : 1,
                        }}
                        className="role-select-custom"
                      >
                        <option value="ADMIN">Admin</option>
                        <option value="OPERATOR">Operator</option>
                        <option value="USER">User</option>
                      </CustomInput>
                    )}
                  </td>
                  <td>{formatDate(member.joinedAt)}</td>
                  <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                    {(() => {
                      const isEnabled = member.notifications?.licenseExpiry !== false;
                      return (
                        <button
                          type="button"
                          onClick={() => toggleMemberNotification(
                            member.id,
                            'licenseExpiry',
                            isEnabled,
                            member.username || member.email
                          )}
                          title={isEnabled ? 'Disable license alerts' : 'Enable license alerts'}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '36px',
                            height: '20px',
                            borderRadius: '10px',
                            border: 'none',
                            backgroundColor: isEnabled ? 'var(--accent-1-color)' : 'rgba(255, 255, 255, 0.2)',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            position: 'relative',
                            padding: 0,
                          }}
                        >
                          <div
                            style={{
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              backgroundColor: '#fff',
                              position: 'absolute',
                              left: isEnabled ? '18px' : '2px',
                              transition: 'left 0.2s',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                            }}
                          />
                        </button>
                      );
                    })()}
                  </td>
                  <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                    {member.isOwner ? (
                      <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>—</span>
                    ) : (
                      <ModalConsumer>
                        {({ showModal }) => (
                          <Button
                            className="btn-primary-custom btn-round"
                            size="sm"
                            style={{ display: 'inline-block' }}
                            disabled={member.id === auth?.uid}
                            onClick={() => {
                              showModal(ConfirmModal, {
                                isOpen: true,
                                title: 'Remove Member',
                                body: `Are you sure you want to remove ${member.username || member.email} from this organization?`,
                                callback: () => {
                                  removeMember(member.id, member.username || member.email);
                                },
                              });
                            }}
                          >
                            <span className="d-flex flex-row align-items-center my-auto">
                              <i className="tim-icons icon-trash-simple"></i>{' '}
                              Remove
                            </span>
                          </Button>
                        )}
                      </ModalConsumer>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default MembersList;
